import React from "react";
import { HiFolder } from "react-icons/hi";
import { useAppSelector } from "../../app/hooks";
import {
  LoadingOverlay,
  useComputedColorScheme,
  useMantineTheme,
} from "@mantine/core";
import { HiMapPin } from "react-icons/hi2";
import ReactCountryFlag from "react-country-flag";
import { useNavigate } from "react-router-dom";

export default function SearchResults(props: {}) {
  const searchState = useAppSelector((state) => state.search);
  const navigate = useNavigate();

  const computedColorScheme = useComputedColorScheme("dark");
  const theme = useMantineTheme();

  const renderResultsSection = (
    sectionName: string,
    content: React.ReactNode
  ) => {
    if (!searchState.simpleSearch) return null;

    return (
      <div className="flex flex-col w-full pb-8 max-w-[700px] mx-auto">
        <div className="text-lg mb-2">{sectionName}</div>
        <div className="flex flex-col w-full">{content}</div>
      </div>
    );
  };

  const renderFolders = () => {
    if (!searchState.folders?.length) return null;
    const content = searchState.folders.map((folder) => {
      return (
        <div
          key={folder.id}
          className="flex flex-row justify-start items-center gap-2 p-1 hover:bg-grey-900 cursor-pointer rounded-lg"
          onClick={() => navigate(`/browse/${folder.id}`)}
        >
          <div className="flex justify-center items-center rounded-full min-w-8 min-h-8">
            <HiFolder className="w-5 h-5"></HiFolder>
          </div>
          <div className="text-md">{folder.name}</div>
        </div>
      );
    });
    return renderResultsSection("Folders", content);
  };

  const renderLocations = () => {
    if (!searchState.locations?.length) return null;
    const content = searchState.locations.map((location) => {
      return (
        <div
          key={location.id}
          className="flex flex-row items-center justify-between sm:items-center 
          gap-2 p-1 pr-4 hover:bg-grey-900 cursor-pointer rounded-lg"
        >
          <div className="flex flex-row justify-start items-center gap-2">
            <div className="flex justify-center items-center rounded-full min-w-8 min-h-8">
              <HiMapPin className="w-5 h-5"></HiMapPin>
            </div>
            <div className="text-md">{location.name}</div>
          </div>
          <div className="text-sm text-grey-600 flex flex-row gap-2 items-baseline">
            <ReactCountryFlag
              style={{ fontFamily: "Twemoji Country Flags" }}
              countryCode={location?.countryCode}
            ></ReactCountryFlag>
            <div>
              {location?.region}, {location?.country}
            </div>
          </div>
        </div>
      );
    });
    return renderResultsSection("Locations", content);
  };

  const renderFiles = () => {
    if (!searchState.files?.length) return null;
    const content = searchState.files.map((file) => {
      return (
        <div
          key={file.id}
          className="flex flex-row justify-start items-center gap-2 p-1 hover:bg-grey-900 cursor-pointer rounded-lg"
        >
          <div className="flex justify-center items-center rounded-full min-w-8 min-h-8">
            <HiFolder className="w-5 h-5"></HiFolder>
          </div>
          <div className="text-md">{file.name}</div>
        </div>
      );
    });
    return renderResultsSection("Files", content);
  };

  const renderPlaceholder = () => {
    const isLoading = searchState.loading;

    if (isLoading)
      return (
        <div className="flex flex-col">
          <LoadingOverlay
            visible={isLoading}
            zIndex={400}
            overlayProps={{
              blur: "xl",
              color: computedColorScheme === "dark" ? "black" : "black",
              opacity: computedColorScheme === "dark" ? "70%" : "30%",
            }}
            loaderProps={{ color: "blue", type: "oval", size: "md" }}
          />
        </div>
      );

    const anyResultsFound =
      searchState.folders?.length || searchState.files?.length;

    if (searchState.isFirstSearch || anyResultsFound) return null;

    return (
      <div className="flex flex-col justify-center items-center text-lg">
        No results found
      </div>
    );
  };

  const renderSectionsOrPlaceholder = () => {
    return (
      <>
        {renderFolders()}
        {renderLocations()}
        {renderFiles()}
        {renderPlaceholder()}
      </>
    );
  };

  return (
    <div className="flex flex-col w-full h-full px-4 sm:px-8 pt-2 pb-8">
      {renderSectionsOrPlaceholder()}
    </div>
  );
}
